import { OutlinedInput } from "@mui/material";
import { useFormat } from "@sinch/core/hooks/format";
import Decimal from "decimal.js-light";
import { isEmpty, isNil } from "ramda";
import React, { forwardRef, ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { FormControlWrapper, TextInputProps } from "@sinch/components";
import { useNumberInputValue } from "@sinch/components/form/hooks/useNumberInputValue";

export type PercentInputProps = Omit<TextInputProps<"number">, "value" | "onChange"> & {
  value: number;
  onChange: (val: number) => void;
};

const PercentInputBase = (
  {
    label,
    FormHelperTextProps: formHelperTextProps,
    FormControlProps: formControlProps,
    helperText,
    errorMessage,
    error,
    required,
    infoTooltip,
    onChange,
    value,
    allowEmpty,
    ...props
  }: PercentInputProps,
  ref: React.Ref<HTMLInputElement>
): ReactElement => {
  const [editMode, setEditMode] = useState(false);
  const internalRef = useRef<HTMLInputElement>(null);
  const { num } = useFormat();

  useEffect(() => {
    if (editMode && internalRef.current) {
      internalRef.current.select();
    }
  }, [editMode]);

  const {
    toClosestLimit,
    toNumber,
    value: numberValue,
  } = useNumberInputValue({
    value,
    allowEmpty,
    onChange: (e) => {
      const decimalValue = sanitizeDecimalInput(e.target.value);
      if (!isNil(decimalValue)) {
        onChange(decimalValue);
      }
    },
    min: props.inputProps?.min,
    max: props.inputProps?.max,
  });

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      props?.onBlur?.(e);
      const newVal = toClosestLimit(e.target.value);
      const sanitizedVal = sanitizeDecimalInput(newVal);
      if (!isNil(sanitizedVal) && sanitizedVal !== value) {
        onChange(sanitizedVal);
      }
      setEditMode(false);
    },
    [props, toClosestLimit, onChange]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (editMode) {
        const newVal = toNumber(e.target.value);
        const sanitizedVal = sanitizeDecimalInput(newVal);
        if (!isNil(sanitizedVal)) {
          onChange(sanitizedVal);
        }
      }
    },
    [editMode, toNumber, onChange]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      internalRef.current?.select();
      setEditMode(true);
      props.onFocus?.(e);
    },
    [props]
  );

  const displayValue = editMode
    ? new Decimal(value).mul(100).toNumber()
    : num.percent(numberValue as number, { maximumFractionDigits: 2 });

  return (
    <FormControlWrapper
      error={error}
      errorMessage={errorMessage}
      FormHelperTextProps={formHelperTextProps}
      helperText={helperText}
      infoTooltip={infoTooltip}
      label={label}
      required={required}
      {...formControlProps}
    >
      <OutlinedInput
        error={error}
        {...props}
        ref={ref}
        fullWidth
        inputProps={{
          ...props.inputProps,
          sx: { ...props.inputProps?.sx, textAlign: "right", "&.MuiInputBase-inputSizeSmall": { px: 1 } },
          ref: internalRef,
        }}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        sx={{ height: "100%", ...props.sx }}
        type={editMode ? "number" : "text"}
        value={displayValue}
      />
    </FormControlWrapper>
  );
};

export const PercentsInput = forwardRef(PercentInputBase) as (
  props: PercentInputProps & { ref?: React.Ref<HTMLInputElement> }
) => ReturnType<typeof PercentInputBase>;

/**
 * Converts a percentage string (0-100 range) to a decimal value (0-1 range)
 * Handles edge cases and ensures safe numeric operations
 */
const sanitizeDecimalInput = (val: string): number | null => {
  if (isEmpty(val)) {
    return 0;
  }

  try {
    const decimal = new Decimal(val);
    const normalized = decimal.toDecimalPlaces(2, Decimal.ROUND_DOWN).div(100);
    const result = normalized.toNumber();

    // Ensure the result is within safe number bounds
    if (!Number.isFinite(result) || String(result).length >= String(Number.MAX_SAFE_INTEGER).length) {
      return null;
    }

    return result;
  } catch {
    return null;
  }
};
